import { cConst, cRange, defaultLayer, gradient, vConst, vCurve, vCurveRandom, vRange } from '../defaults.js';
import { curvePreset } from '../math/curve.js';
/**
 * Reference effect #1: coin pickup (plan §"Reference effects").
 * Pop flash → golden coin body arcing up with overshoot scale → sparkle burst
 * → lingering rising twinkles. Choreographed with layer delays, not physics.
 */
export const coinPopEffect = {
    version: 1,
    meta: { name: 'Coin Pickup', duration: 1.2, loop: false, simulationSpace: 'local' },
    layers: [
        defaultLayer({
            id: 'coin_flash',
            name: 'flash',
            delay: 0,
            duration: 0.1,
            maxParticles: 4,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(0.18),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(1.1),
                    rotation: vConst(0),
                    color: cConst([1, 0.98, 0.85, 0.9]),
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('pulse') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [0.4, [1, 0.9, 0.4, 0.8]], [1, [1, 0.7, 0.2, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'glow' } },
        }),
        defaultLayer({
            id: 'coin_body',
            name: 'coin pop',
            delay: 0,
            duration: 0.1,
            maxParticles: 4,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'cone', angle: 0, radius: 0 }, // straight up
                initial: {
                    lifetime: vConst(0.55),
                    speed: vConst(3.2), // arcs back down under gravity
                    spread: vConst(0),
                    size: vConst(0.42),
                    rotation: vConst(0),
                    angularVelocity: vConst(520),
                    color: cConst([1, 0.85, 0.25, 1]),
                },
            },
            modules: [
                { type: 'gravity', enabled: true, strength: 12 },
                // Overshoot: the coin pops to 130% then settles — squash & stretch principle #3.
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOutBack') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [0.75, [1, 1, 0.9, 1]], [1, [1, 0.9, 0.6, 0]]),
                },
            ],
            material: { blend: 'alpha', sprite: { type: 'builtin', id: 'hardDot' } },
        }),
        defaultLayer({
            id: 'coin_death_sparkles',
            name: 'sparkle on coin death',
            delay: 0,
            duration: 1.2,
            maxParticles: 24,
            emitter: {
                // sub-emitter (plan §3.2): fires wherever the coin body dies
                trigger: { parent: 'coin_body', on: 'death' },
                mode: { type: 'burst', count: 10 },
                shape: { type: 'sphere', radius: 0.05, shell: true },
                initial: {
                    lifetime: vRange(0.25, 0.5),
                    speed: vRange(1.2, 2.6),
                    spread: vConst(8),
                    size: vRange(0.07, 0.16),
                    rotation: vRange(0, 360),
                    angularVelocity: vRange(-360, 360),
                    color: cRange([1, 1, 0.8, 1], [1, 0.85, 0.3, 1]),
                },
            },
            modules: [
                { type: 'drag', enabled: true, coefficient: 3 },
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('pulse') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [1, [1, 0.8, 0.3, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'star' } },
        }),
        defaultLayer({
            id: 'coin_sparkles',
            name: 'sparkle burst',
            delay: 0.05,
            duration: 0.1,
            maxParticles: 32,
            emitter: {
                mode: { type: 'burst', count: 14 },
                shape: { type: 'sphere', radius: 0.15, shell: true },
                initial: {
                    lifetime: vRange(0.35, 0.6),
                    speed: vRange(1.6, 3.4),
                    spread: vConst(10),
                    size: vRange(0.1, 0.22),
                    rotation: vRange(0, 360),
                    color: cRange([1, 0.95, 0.5, 1], [1, 0.75, 0.2, 1]),
                },
            },
            modules: [
                { type: 'gravity', enabled: true, strength: 5 },
                { type: 'drag', enabled: true, coefficient: 2.5 },
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('pulse') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [0.6, [1, 0.9, 0.5, 1]], [1, [1, 0.6, 0.1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'star' } },
        }),
        defaultLayer({
            id: 'coin_twinkle',
            name: 'rising twinkles',
            delay: 0.12,
            duration: 0.45,
            maxParticles: 24,
            emitter: {
                mode: { type: 'rate', perSecond: 26 },
                shape: { type: 'cone', angle: 18, radius: 0.28 },
                initial: {
                    lifetime: vRange(0.3, 0.55),
                    // launch speed eases off as the burst winds down (birth-time curve provider)
                    speed: vCurveRandom(curvePreset('easeOut'), 0.6, 1.4),
                    spread: vConst(8),
                    size: vRange(0.06, 0.14),
                    rotation: vRange(0, 360),
                    color: cConst([1, 0.95, 0.6, 1]),
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('flicker') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.15, [1, 1, 1, 1]], [1, [1, 0.85, 0.4, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'star' } },
        }),
    ],
};
